import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { endpoints } from './endpoints'
import { keys } from './keys'
import { toCapacityEnvelope } from './adapters'
import type { PaymentsActivityDto, PaymentsSummaryDto } from './payments'
import type { CapacityEnvelope } from '../core/capacityEnvelope'
import type { AccountDto, AdminUsersDto, BidsDto, CapacityEnvelopeDto, CatalogDto, ChainDto, DeploymentDetailDto, DeploymentListDto, EscrowDto, IdentityListDto, LeaseListDto, MeDto, MyIpDto, OperatorContractDto, OperatorDeploymentsDto, OperatorFleetDto, OperatorLogDto, OperatorNodeDto, OperatorProviderDto, ProviderDto, ProviderListDto } from './types'

export interface QueryState<T> {
  data: T | undefined
  error: unknown
  isFetching: boolean
  isLoading: boolean
  isStale: boolean
  refetch: () => Promise<T | undefined>
}

function state<T>(query: UseQueryResult<T, unknown>): QueryState<T> {
  return { data: query.data, error: query.error, isFetching: query.isFetching, isLoading: query.isLoading, isStale: query.isStale, refetch: async () => (await query.refetch()).data }
}

export function useChain(enabled = true): QueryState<ChainDto> {
  return state(useQuery({ queryKey: keys.chain, queryFn: ({ signal }) => endpoints.chain({ signal }), enabled, staleTime: 30_000, refetchInterval: 12_000 }))
}

/** One provider's on-chain record, including the attributes placement matches against. */
export function useProvider(address: string | undefined, enabled = true): QueryState<ProviderDto> {
  return state(useQuery({ queryKey: keys.provider(address ?? ''), queryFn: ({ signal }) => endpoints.provider(address!, { signal }), enabled: enabled && Boolean(address), staleTime: 60_000 }))
}

export function useProviders(enabled = true): QueryState<ProviderListDto> {
  return state(useQuery({ queryKey: keys.providers, queryFn: ({ signal }) => endpoints.providers({ signal }), enabled, staleTime: 30_000 }))
}

export function useCatalog(params: URLSearchParams): QueryState<CatalogDto> {
  const serialized = params.toString()
  return state(useQuery({ queryKey: keys.catalog(serialized), queryFn: ({ signal }) => endpoints.catalog(params, { signal }), staleTime: 60_000 }))
}

export function useCapacityEnvelope(enabled = true): QueryState<CapacityEnvelope> {
  const query = state(useQuery({
    queryKey: keys.capacityEnvelope,
    queryFn: ({ signal }) => endpoints.capacityEnvelope({ signal }),
    enabled,
    staleTime: 30_000,
    refetchInterval: 45_000,
  })) as QueryState<CapacityEnvelopeDto>
  return {
    ...query,
    data: query.data ? toCapacityEnvelope(query.data) : undefined,
    refetch: async () => {
      const next = await query.refetch()
      return next ? toCapacityEnvelope(next) : undefined
    },
  }
}

export function useAccount(address: string | undefined): QueryState<AccountDto> {
  return state(useQuery({ queryKey: keys.account(address ?? ''), queryFn: ({ signal }) => endpoints.account(address ?? '', { signal }), enabled: Boolean(address), staleTime: 15_000, refetchInterval: 15_000 }))
}

export function useDeployments(address: string | undefined): QueryState<DeploymentListDto> {
  return state(useQuery({ queryKey: keys.deployments(address ?? ''), queryFn: ({ signal }) => endpoints.deployments(address ?? '', { signal }), enabled: Boolean(address), staleTime: 10_000, refetchInterval: 10_000 }))
}

export function useDeployment(address: string | undefined, dseq: string | undefined): QueryState<DeploymentDetailDto> {
  return state(useQuery({ queryKey: keys.deployment(address ?? '', dseq ?? ''), queryFn: ({ signal }) => endpoints.deployment(address ?? '', dseq ?? '', { signal }), enabled: Boolean(address && dseq), staleTime: 10_000, refetchInterval: 10_000 }))
}

export function useLeases(address: string | undefined): QueryState<LeaseListDto> {
  return state(useQuery({ queryKey: keys.leases(address ?? ''), queryFn: ({ signal }) => endpoints.leases(address ?? '', { signal }), enabled: Boolean(address), staleTime: 10_000, refetchInterval: 10_000 }))
}

export function useEscrow(address: string | undefined): QueryState<EscrowDto> {
  return state(useQuery({ queryKey: keys.escrow(address ?? ''), queryFn: ({ signal }) => endpoints.escrow(address ?? '', { signal }), enabled: Boolean(address), staleTime: 15_000, refetchInterval: 15_000 }))
}

export function useBids(address: string | undefined, dseq: string | undefined): QueryState<BidsDto> {
  return state(useQuery({ queryKey: keys.bids(address ?? '', dseq ?? ''), queryFn: ({ signal }) => endpoints.bids(address ?? '', dseq ?? '', { signal }), enabled: Boolean(address && dseq), refetchInterval: 3_000 }))
}

/** The caller's public address, for "Add my IP". Fetched only once a port is restricted. */
export function useMyIp(enabled = true): QueryState<MyIpDto> {
  return state(useQuery({ queryKey: keys.myIp, queryFn: ({ signal }) => endpoints.myIp({ signal }), enabled, staleTime: 5 * 60_000, retry: false }))
}

export function useMe(enabled = true): QueryState<MeDto> {
  return state(useQuery({ queryKey: keys.me, queryFn: ({ signal }) => endpoints.me({ signal }), enabled, staleTime: 30_000 }))
}

export function useIdentities(enabled = true): QueryState<IdentityListDto> {
  return state(useQuery({ queryKey: keys.identities, queryFn: ({ signal }) => endpoints.identities({ signal }), enabled, staleTime: 30_000 }))
}

export function useOperatorNodes(enabled = true): QueryState<OperatorNodeDto[]> {
  return state(useQuery({ queryKey: keys.operator('nodes'), queryFn: ({ signal }) => endpoints.operator.nodes({ signal }), enabled, refetchInterval: 12_000 }))
}

export function useOperatorProviders(enabled = true): QueryState<OperatorProviderDto[]> {
  return state(useQuery({ queryKey: keys.operator('providers'), queryFn: ({ signal }) => endpoints.operator.providers({ signal }), enabled, staleTime: 30_000 }))
}

export function useOperatorFleet(enabled = true): QueryState<OperatorFleetDto> {
  return state(useQuery({ queryKey: keys.operator('fleet'), queryFn: ({ signal }) => endpoints.operator.fleet({ signal }), enabled, staleTime: 30_000 }))
}

export function useOperatorContract(): QueryState<OperatorContractDto> {
  return state(useQuery({ queryKey: keys.operator('contract'), queryFn: ({ signal }) => endpoints.operator.contract({ signal }), staleTime: 15_000 }))
}

export function useOperatorLogs(enabled = true): QueryState<OperatorLogDto[]> {
  return state(useQuery({ queryKey: keys.operator('logs'), queryFn: ({ signal }) => endpoints.operator.logs({ signal }), enabled, refetchInterval: 3_000 }))
}

export function useOperatorDeployments(enabled = true): QueryState<OperatorDeploymentsDto> {
  return state(useQuery({ queryKey: keys.operator('deployments'), queryFn: ({ signal }) => endpoints.operator.deployments({ signal }), enabled, refetchInterval: 8_000 }))
}

export function useOperatorPods(enabled = true): QueryState<unknown[]> {
  return state(useQuery({ queryKey: keys.operator('pods'), queryFn: ({ signal }) => endpoints.operator.pods({ signal }), enabled, staleTime: 10_000 }))
}

export function useOperatorServices(enabled = true): QueryState<unknown[]> {
  return state(useQuery({ queryKey: keys.operator('services'), queryFn: ({ signal }) => endpoints.operator.services({ signal }), enabled, staleTime: 10_000 }))
}

export function useOperatorNodeports(enabled = true): QueryState<unknown[]> {
  return state(useQuery({ queryKey: keys.operator('nodeports'), queryFn: ({ signal }) => endpoints.operator.nodeports({ signal }), enabled, staleTime: 10_000 }))
}

export function useAdminUsers(enabled = true): QueryState<AdminUsersDto> {
  return state(useQuery({ queryKey: keys.operator('users'), queryFn: ({ signal }) => endpoints.operator.users({ signal }), enabled, staleTime: 10_000 }))
}

/**
 * The tenant's dollar balance.
 *
 * Polls faster while a funding is in flight: the tenant is watching this number
 * change and a 15-second wait to learn their money arrived reads as broken.
 * Falls back to a slow poll once nothing is pending, because this endpoint
 * costs a chain query on the server.
 */
export function usePaymentsSummary(enabled = true): QueryState<PaymentsSummaryDto> {
  return state(
    useQuery({
      queryKey: keys.paymentsSummary,
      queryFn: ({ signal }) => endpoints.payments.summary({ signal }),
      enabled,
      staleTime: 5_000,
      refetchInterval: (query) => (query.state.data?.pending_funding ? 3_000 : 20_000),
    }),
  )
}

export function usePaymentsActivity(enabled = true): QueryState<PaymentsActivityDto> {
  return state(
    useQuery({
      queryKey: keys.paymentsActivity,
      queryFn: ({ signal }) => endpoints.payments.activity({ signal }),
      enabled,
      staleTime: 10_000,
    }),
  )
}
